import {
  RELATION_PART_OF,
  parseEntityRef,
  stringifyEntityRef,
  type Entity,
} from '@backstage/catalog-model';
import { hasTechDocs } from '../../catalog/hasTechDocs';
import { readerPath } from '../readerPath';
import type { DocTreeEntity, DocTreeNode } from './types';

/** Bucket key for a System with no Domain, or an entity with no System. */
const UNASSIGNED = 'unassigned';
const UNASSIGNED_TITLE = 'Unassigned';

const byTitle = (a: { title: string }, b: { title: string }) =>
  a.title.localeCompare(b.title);

/** `Unassigned` sorts last regardless of where its label would fall alphabetically. */
const byTitleUnassignedLast = (a: DocTreeNode, b: DocTreeNode) => {
  if (a.key === UNASSIGNED) return 1;
  if (b.key === UNASSIGNED) return -1;
  return byTitle(a, b);
};

const TYPED_KINDS = new Set(['component', 'api']);

const toTreeEntity = (entity: Entity): DocTreeEntity => ({
  entityRef: stringifyEntityRef(entity),
  kind: entity.kind,
  title: entity.metadata.title ?? entity.metadata.name,
  hasDocs: hasTechDocs(entity),
  path: readerPath(entity),
  type: TYPED_KINDS.has(entity.kind.toLowerCase())
    ? (entity.spec?.type as string | undefined)
    : undefined,
  lifecycle: entity.spec?.lifecycle as string | undefined,
});

/** Plural folder label for a leaf kind; falls back to a naive `+s` for an unknown kind. */
const KIND_GROUP_TITLES: Record<string, string> = {
  api: 'APIs',
  component: 'Components',
  repository: 'Repositories',
  resource: 'Resources',
  group: 'Teams',
};
const kindGroupTitle = (kind: string): string =>
  KIND_GROUP_TITLES[kind.toLowerCase()] ?? `${kind}s`;

/**
 * Splits a System's (or the synthetic Unassigned system's) entities into one
 * folder node per kind — Components with Components, Repositories with
 * Repositories — the same shape as a Domain's Systems, just one level deeper.
 */
const groupEntitiesByKind = (
  parentKey: string,
  entities: DocTreeEntity[],
): DocTreeNode[] => {
  const byKind = new Map<string, DocTreeEntity[]>();
  entities.forEach(entity => {
    const kind = entity.kind.toLowerCase();
    byKind.set(kind, [...(byKind.get(kind) ?? []), entity]);
  });
  return [...byKind.entries()]
    .map(([kind, group]): DocTreeNode => ({
      key: `${parentKey}#${kind}`,
      title: kindGroupTitle(kind),
      children: [],
      entities: group.sort(byTitle),
    }))
    .sort(byTitle);
};

/** The ref of the entity's parent of `kind`, via the `partOf` relation. */
const relationTarget = (entity: Entity, kind: string): string | undefined =>
  entity.relations?.find(
    r =>
      r.type === RELATION_PART_OF &&
      parseEntityRef(r.targetRef).kind.toLowerCase() === kind,
  )?.targetRef;

const TEAMS_KEY = 'org#teams';
const PODS_KEY = 'org#pods';

/** `care-delivery` -> `Care Delivery`. */
const humanize = (slug: string): string =>
  slug
    .split('-')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

/**
 * Documented Groups, out of the Domain hierarchy: a `Teams` folder holding
 * every team, and a `Pods` folder with one sub-folder per pod family. The
 * family is the Group's first tag (stamped by the githubOrg team transformer
 * from `honeGithubOrg.pods`); a pod with no tag files directly under `Pods`.
 */
function buildGroupNodes(groups: Entity[]): DocTreeNode[] {
  const isPod = (group: Entity) => group.spec?.type === 'pod';
  const teams = groups.filter(g => !isPod(g)).map(toTreeEntity);
  const pods = groups.filter(isPod);

  const byFamily = new Map<string, DocTreeEntity[]>();
  const untagged: DocTreeEntity[] = [];
  pods.forEach(pod => {
    const family = pod.metadata.tags?.[0];
    if (!family) {
      untagged.push(toTreeEntity(pod));
      return;
    }
    byFamily.set(family, [...(byFamily.get(family) ?? []), toTreeEntity(pod)]);
  });

  const familyNodes = [...byFamily.entries()]
    .map(([family, members]): DocTreeNode => ({
      key: `${PODS_KEY}#${family}`,
      title: humanize(family),
      children: [],
      entities: members.sort(byTitle),
    }))
    .sort(byTitle);

  const nodes: DocTreeNode[] = [];
  if (teams.length) {
    nodes.push({
      key: TEAMS_KEY,
      title: 'Teams',
      children: [],
      entities: teams.sort(byTitle),
    });
  }
  if (familyNodes.length || untagged.length) {
    nodes.push({
      key: PODS_KEY,
      title: 'Pods',
      children: familyNodes,
      entities: untagged.sort(byTitle),
    });
  }
  return nodes;
}

/**
 * Every *documented* catalog entity, nested Domain -> System -> kind folder
 * (Components, Repositories, APIs, Resources) -> entity. A System or entity
 * with no parent files under a synthetic `Unassigned` bucket at that level,
 * rather than being dropped. A Domain or System with no techdocs-ref of its
 * own and no documented entity under it is a dead end and is dropped
 * entirely, not just left unclickable.
 */
export function buildDocTree(entities: Entity[]): DocTreeNode[] {
  const isKind = (entity: Entity, kind: string) =>
    entity.kind.toLowerCase() === kind;

  const domains = entities.filter(e => isKind(e, 'domain'));
  const systems = entities.filter(e => isKind(e, 'system'));
  const groups = entities.filter(e => isKind(e, 'group') && hasTechDocs(e));
  const leaves = entities.filter(
    e =>
      !isKind(e, 'domain') &&
      !isKind(e, 'system') &&
      !isKind(e, 'group') &&
      hasTechDocs(e),
  );

  const domainByRef = new Map(domains.map(d => [stringifyEntityRef(d), d]));
  const systemByRef = new Map(systems.map(s => [stringifyEntityRef(s), s]));

  const entitiesBySystem = new Map<string, Entity[]>();
  leaves.forEach(entity => {
    const system = relationTarget(entity, 'system') ?? UNASSIGNED;
    entitiesBySystem.set(system, [
      ...(entitiesBySystem.get(system) ?? []),
      entity,
    ]);
  });

  const systemsByDomain = new Map<string, Entity[]>();
  systems.forEach(system => {
    const domain = relationTarget(system, 'domain') ?? UNASSIGNED;
    systemsByDomain.set(domain, [...(systemsByDomain.get(domain) ?? []), system]);
  });

  // A System with none of its own docs and no documented entities under it
  // is a dead end and is dropped, the same as an undocumented leaf.
  const buildSystemNode = (system: Entity): DocTreeNode | undefined => {
    const key = stringifyEntityRef(system);
    const systemEntities = (entitiesBySystem.get(key) ?? []).map(toTreeEntity);
    if (systemEntities.length === 0 && !hasTechDocs(system)) {
      return undefined;
    }
    return {
      key,
      title: system.metadata.title ?? system.metadata.name,
      entity: toTreeEntity(system),
      children: groupEntitiesByKind(key, systemEntities),
      entities: [],
    };
  };

  // Entities whose System is itself unassigned have no real System node to
  // hang off, so they get one: a synthetic "Unassigned" system, filed under
  // the synthetic "Unassigned" domain alongside any domain-less Systems.
  const unassignedLeaves = entitiesBySystem.get(UNASSIGNED) ?? [];
  const unassignedSystemKey = `${UNASSIGNED}-system`;
  const unassignedSystemNode: DocTreeNode | undefined = unassignedLeaves.length
    ? {
        key: unassignedSystemKey,
        title: UNASSIGNED_TITLE,
        children: groupEntitiesByKind(
          unassignedSystemKey,
          unassignedLeaves.map(toTreeEntity),
        ),
        entities: [],
      }
    : undefined;

  // An entity's `partOf` can name a System ref that no longer resolves to an
  // actual System entity (renamed/deleted, or catalog drift) — its System's
  // own domain is then unknowable, so it's filed as its own dangling-System
  // node under Unassigned instead of disappearing from the tree entirely.
  const danglingSystemNodes: DocTreeNode[] = [...entitiesBySystem.keys()]
    .filter(key => key !== UNASSIGNED && !systemByRef.has(key))
    .map(
      (key): DocTreeNode => ({
        key,
        title: parseEntityRef(key).name,
        children: groupEntitiesByKind(
          key,
          (entitiesBySystem.get(key) ?? []).map(toTreeEntity),
        ),
        entities: [],
      }),
    )
    .sort(byTitle);

  const domainKeys = new Set([
    ...domains.map(d => stringifyEntityRef(d)),
    ...systemsByDomain.keys(),
    UNASSIGNED,
  ]);

  const orgNodes = buildGroupNodes(groups);

  const domainNodes = [...domainKeys]
    .map((domainRef): DocTreeNode | undefined => {
      const domain = domainByRef.get(domainRef);
      const systemNodes = (systemsByDomain.get(domainRef) ?? [])
        .map(buildSystemNode)
        .filter((node): node is DocTreeNode => node !== undefined)
        .sort(byTitle);

      if (domainRef === UNASSIGNED) {
        if (unassignedSystemNode) systemNodes.push(unassignedSystemNode);
        systemNodes.push(...danglingSystemNodes);
      }
      // A Domain with no documented Systems is only kept if it carries its
      // own docs; otherwise, like a System, it's a dead end and is dropped.
      if (systemNodes.length === 0 && !(domain && hasTechDocs(domain))) {
        return undefined;
      }

      return {
        key: domainRef,
        title:
          domain?.metadata.title ??
          domain?.metadata.name ??
          (domainRef === UNASSIGNED
            ? UNASSIGNED_TITLE
            : parseEntityRef(domainRef).name),
        entity: domain ? toTreeEntity(domain) : undefined,
        children: systemNodes,
        entities: [],
      };
    })
    .filter((node): node is DocTreeNode => node !== undefined)
    .sort(byTitleUnassignedLast);

  // Teams and Pods sit between the real Domains and the Unassigned bucket.
  const unassignedIndex = domainNodes.findIndex(n => n.key === UNASSIGNED);
  const insertAt = unassignedIndex === -1 ? domainNodes.length : unassignedIndex;
  return [
    ...domainNodes.slice(0, insertAt),
    ...orgNodes,
    ...domainNodes.slice(insertAt),
  ];
}
